import useProviderType from '@app/src/hooks/useProviderType'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { Box, List, ListItem, ListItemButton, ListItemText, Radio, Stack, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

type AssessmentOptionProps = {
  template: AssessmentTemplate
  isSelected: boolean
  onSelect: () => void
}

const AssessmentOption: React.FC<AssessmentOptionProps> = ({ template, isSelected, onSelect }) => {
  const { formatMessage } = useIntl()
  const providerType = useProviderType()
  const isBaseline = template.assessmentType === AssessmentType.BaselineAssessment

  const title = isBaseline
    ? formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.baselineTitle' })
    : template.name
  const description = isBaseline
    ? formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.baselineDescription' }, { providerType })
    : template.description

  return (
    <ListItem
      disableGutters
      disablePadding
      sx={{
        border: isSelected ? 2 : 1,
        borderColor: isSelected ? 'text.primary' : 'divider',
        borderRadius: 2,
        mb: 1,
      }}
    >
      <ListItemButton onClick={onSelect} sx={{ px: 2, py: 1, borderRadius: 2 }}>
        <Radio checked={isSelected} color="primary" sx={{ mr: 1 }} inputProps={{ 'aria-label': title }} />
        <ListItemText primary={title} secondary={description} />
      </ListItemButton>
    </ListItem>
  )
}

type SelectAssessmentStepProps = {
  assessmentTemplates: AssessmentTemplate[]
  selectedTemplateId: number | null
  onSelect: (template: AssessmentTemplate) => void
  showComingSoon: boolean
}

const SelectAssessmentStep: React.FC<SelectAssessmentStepProps> = ({
  assessmentTemplates,
  selectedTemplateId,
  onSelect,
  showComingSoon,
}) => {
  const { formatMessage } = useIntl()

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">
        {formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.sectionLabel' })}
      </Typography>
      <List disablePadding>
        {assessmentTemplates.map(template => (
          <AssessmentOption
            key={template.id}
            template={template}
            isSelected={template.id === selectedTemplateId}
            onSelect={() => onSelect(template)}
          />
        ))}
        {showComingSoon && (
          <ListItem
            disableGutters
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              px: 2,
              py: 1,
            }}
          >
            <Radio
              disabled
              sx={{ mr: 1 }}
              inputProps={{
                'aria-label': formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.comingSoonTitle' }),
              }}
            />
            <ListItemText
              primary={
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  {formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.comingSoonTitle' })}
                </Box>
              }
              secondary={formatMessage({ id: 'bulkCreateExpectation.steps.selectAssessment.comingSoonDescription' })}
              secondaryTypographyProps={{ color: 'text.disabled' }}
            />
          </ListItem>
        )}
      </List>
    </Stack>
  )
}

export default SelectAssessmentStep
